import type { Environment } from '@/utils/environmentUtils'
import type { DashboardDeployment } from '@/services/dashboard-deployments'
import type { DashboardTarget } from '@/types/ui-settings.types'

/** A merchant-dashboard served from the developer's own machine, reachable only from dev ADX. */
export const LOCAL_DEV_DASHBOARD_URL = 'https://local-dashboard.dev.tattle-internal.net:9080'

/** Deployments older than this are hidden until the user asks to see them all. */
export const RECENT_DEPLOYMENT_WINDOW_DAYS = 14

/** The branch that is already the default staging dashboard, so offering it again adds nothing. */
const SHARED_STAGING_BRANCH = 'staging'

// The admin's access token rides in the login URL, so only an Amplify origin is ever acceptable.
const AMPLIFY_ORIGIN_PATTERN = /^https:\/\/[a-z0-9-]+\.[a-z0-9]+\.amplifyapp\.com$/

export const DEFAULT_DASHBOARD_TARGET: DashboardTarget = { kind: 'default' }

export const isValidDeploymentUrl = (url: unknown): url is string =>
  typeof url === 'string' && AMPLIFY_ORIGIN_PATTERN.test(url)

/**
 * The target kinds each environment may use. Production offers none: staff there must only
 * ever reach the real dashboard.
 */
export const isDashboardTargetSupported = (environment: Environment): boolean =>
  environment === 'DEV' || environment === 'STAGING'

export const isDashboardTargetAllowed = (target: DashboardTarget, environment: Environment): boolean => {
  switch (target.kind) {
    case 'default':
      return true
    case 'local':
      return environment === 'DEV'
    case 'deployment':
      return environment === 'STAGING' && isValidDeploymentUrl(target.url)
    default:
      return false
  }
}

/**
 * The URL a target sends Login as merchant to, or null when it should use the normal default.
 */
export const resolveDashboardTargetUrl = (
  target: DashboardTarget,
  environment: Environment
): string | null => {
  if (!isDashboardTargetAllowed(target, environment)) return null
  if (target.kind === 'local') return LOCAL_DEV_DASHBOARD_URL
  if (target.kind === 'deployment') return target.url
  return null
}

/**
 * Normalises whatever was stored into a known target shape, so a hand-edited or outdated
 * settings object can never smuggle an unchecked URL through.
 */
export const parseDashboardTarget = (value: unknown): DashboardTarget => {
  if (typeof value !== 'object' || value === null) return DEFAULT_DASHBOARD_TARGET
  const candidate = value as Record<string, unknown>
  if (candidate.kind === 'local') return { kind: 'local' }
  if (
    candidate.kind === 'deployment' &&
    typeof candidate.branch === 'string' &&
    candidate.branch.length > 0 &&
    isValidDeploymentUrl(candidate.url)
  ) {
    return { kind: 'deployment', branch: candidate.branch, url: candidate.url }
  }
  return DEFAULT_DASHBOARD_TARGET
}

/**
 * Deployments a staging admin can log in to: on the staging backend, last build succeeded,
 * and not the shared staging branch (that one is Default).
 */
export const getSelectableDeployments = (deployments: DashboardDeployment[]): DashboardDeployment[] =>
  deployments.filter(
    deployment =>
      deployment.backendEnvironment === 'staging' &&
      deployment.lastBuild?.status === 'succeeded' &&
      deployment.branch !== SHARED_STAGING_BRANCH &&
      isValidDeploymentUrl(deployment.url)
  )

export interface DeploymentListOptions {
  showAll: boolean
  search: string
  now?: Date
}

/**
 * The picker's list: selectable deployments, recent ones only unless showAll, narrowed by a
 * branch-name search, newest first.
 */
export const filterDeploymentsForPicker = (
  deployments: DashboardDeployment[],
  { showAll, search, now = new Date() }: DeploymentListOptions
): DashboardDeployment[] => {
  const cutoff = now.getTime() - RECENT_DEPLOYMENT_WINDOW_DAYS * 24 * 60 * 60 * 1000
  const term = search.trim().toLowerCase()

  return getSelectableDeployments(deployments)
    .filter(deployment => showAll || new Date(deployment.updatedAt).getTime() >= cutoff)
    .filter(deployment => !term || deployment.branch.toLowerCase().includes(term))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
}

/**
 * Whether a saved deployment target is still one the list offers, matched on branch AND url
 * so a stale URL for a re-created branch is not reused.
 */
export const isTargetInDeployments = (
  target: DashboardTarget,
  deployments: DashboardDeployment[]
): boolean => {
  if (target.kind !== 'deployment') return true
  return getSelectableDeployments(deployments).some(
    deployment => deployment.branch === target.branch && deployment.url === target.url
  )
}
